import moment from 'moment';
import { getPreSignedUrl } from '../services';
import { FilteringSortingParams } from './type';
import { AI_MEDIA_GENERATE_ERR_MSGS } from './constant';

export function isVideo(fileDetails: any): boolean {
  return fileDetails?.type?.includes('video') || false;
}

export function isImage(fileDetails: any): boolean {
  return fileDetails?.type?.includes('image') || false;
}

export const extractObjectId = (value: string): string => {
  try {
    const url = new URL(value);
    const objectId = url.pathname.split('/').pop();
    return objectId;
  } catch (error) {
    console.error(error.message);
    return '';
  }
};

export const extractDate = (value: string): string => {
  // Use ISO format for date parsing to avoid deprecation warnings
  return value ? moment(new Date(value).toISOString()).format('MM-DD-YY') : '';
};

export const extractTime = (value: string): string => {
  // Use ISO format for date parsing to avoid deprecation warnings
  return value ? moment(new Date(value).toISOString()).format('h:mm A') : '';
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const extractNameFromEmail = (value: string): string => {
  return value.split('@')[0];
};

export const isCreditsAvailable = (
  availableCredits: number,
  creditsInUse: number,
  requiredCredits: number
) => {
  return availableCredits - creditsInUse >= requiredCredits;
};

export const downloadFile = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // blob clean up
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.log(error);
  }
};

export const showCredits = (usableCredits) =>
  usableCredits === null
    ? ''
    : Number(usableCredits) <= 0
    ? 0
    : usableCredits
    ? (Number.isInteger(usableCredits) ? usableCredits : Math.floor(usableCredits * 1000) / 1000)
        .toString()
        .replace(/(\.\d*?)0+$/, '$1')
        .replace(/\.$/, '')
    : 0;

export const calculateUsableCredits = (credits) => {
  const value = credits.availableCredits - credits.creditsInUse;
  return value > 0 ? value : 0;
};

export const checkValidContent = (text: string) => {
  const wordsLength = text.split(' ').length;
  const charactersLength = text.split('').length;
  const maxAssumedWordLength = 25;
  const assumedWordsToBe = charactersLength / maxAssumedWordLength;
  if (assumedWordsToBe > wordsLength) {
    return false;
  }
  return true;
};

export const getRandomInt = (min: number, max: number): Number => {
  const minCeiled = Math.ceil(min);
  const maxFloored = Math.floor(max);
  return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled); // The maximum is exclusive and the minimum is inclusive
};

export const isInputEmpty = (str: string): boolean => !str || !str.trim().length;

export const formatTimeFromFrames = (frames: number, fps: number) => {
  const totalSeconds = Math.floor(frames / fps);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const format = (num: number) => String(num).padStart(2, '0');
  return `${format(minutes)}:${format(seconds)}`;
};

export const handleDownloadVideoFile = async ({
  url,
  name,
  onSuccess = () => {},
  format = 'video',
}: {
  url: string;
  name: string;
  onSuccess?: () => void;
  format?: 'video' | 'image' | 'audio';
}) => {
  const link = document.createElement('a');
  const objectId = extractObjectId(url);
  const playableUrl = await getPreSignedUrl({
    format: format,
    objectId: objectId,
    name: `${name}.${format === 'image' ? 'jpg' : format === 'audio' ? 'mp3' : 'mp4'}`,
  });
  link.href = playableUrl;
  link.download = 'filename.mp4';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  onSuccess();
};

export const createFilteringSortingUrlParams = (
  params: FilteringSortingParams
): URLSearchParams => {
  const { page = 1, limit, sortBy = 'updatedAt', sortOrder = 'desc', filters = {} } = params;
  const searchParams = new URLSearchParams();

  if (page) searchParams.append('page', page.toString());
  if (limit) searchParams.append('limit', limit.toString());
  if (sortBy) searchParams.append('sortBy', sortBy);
  if (sortOrder) searchParams.append('sortOrder', sortOrder);

  if (Object.keys(filters).length) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        searchParams.append(`filters[${key}]`, value);
      }
    });
  }

  return searchParams;
};

export const getErrorMsgForAiMediaGeneration = (error: string, defaultErrMessage: string) => {
  for (let err of AI_MEDIA_GENERATE_ERR_MSGS) {
    if (error.includes(err)) return error;
  }
  return defaultErrMessage;
};
